// Traer la libreta de Outlook u OX: se elige el archivo, se ve cuantos entran y se importan sin repetir correos.
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { correosExistentes, importarContactos, type Avance } from '../api/libreta';
import { Icon } from '../lib/icons';
import { esExcel, filasDeArchivo } from '../lib/excel';
import { clasificar, decodificar, deFilas, leerLibreta, type ContactoLibreta } from '../lib/libreta';
import { Dialog } from './Dialog';
import { toast } from './toasts';

type Revision = { archivo: string; nuevos: ContactoLibreta[]; repetidos: number; incompletos: number };

export function ImportarContactos({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [rev, setRev] = useState<Revision | null>(null);
  const [leyendo, setLeyendo] = useState(false);
  const [avance, setAvance] = useState<Avance | null>(null);
  const [error, setError] = useState('');

  const elegir = async (archivo: File | undefined) => {
    if (!archivo) return;
    setError('');
    setLeyendo(true);
    try {
      const contactos = esExcel(archivo.name) ? deFilas(await filasDeArchivo(archivo)) : leerLibreta(archivo.name, decodificar(await archivo.arrayBuffer()));
      if (!contactos.length) throw new Error('No encontré contactos en ese archivo. Debe ser .vcf (correo web) o .csv (Outlook).');
      const existentes = await correosExistentes(contactos.map((c) => c.email));
      setRev({ archivo: archivo.name, ...clasificar(contactos, existentes) });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLeyendo(false);
    }
  };

  const importar = async () => {
    if (!rev) return;
    const fin = await importarContactos(rev.nuevos, setAvance);
    void qc.invalidateQueries();
    toast(`Importados ${fin.hechos - fin.fallidos.length} contactos${fin.empresasNuevas ? ` y ${fin.empresasNuevas} empresas nuevas` : ''}.`);
  };

  const terminado = !!avance && avance.hechos === avance.total;
  return (
    <Dialog titulo="Importar contactos" onClose={onClose} width={560}>
      <div className="form">
        {!rev ? (
          <>
            <ol className="pasos">
              <li>
                <b>Outlook:</b> Archivo → Abrir y exportar → Importar o exportar → Exportar a un archivo → Valores separados por comas → Contactos.
              </li>
              <li>
                <b>Correo web (OX):</b> Contactos → clic derecho en la carpeta → Exportar → vCard.
              </li>
              <li>
                <b>Excel:</b> usa el <a href="/formatos/contactos.xlsx" download>formato de ejemplo</a> (Nombre, Apellido, Correo, Teléfono, Empresa, Cargo). Cada
                fila necesita correo o teléfono.
              </li>
              <li>Elige aquí el archivo que guardaste.</li>
            </ol>
            <label className="btn primary" style={{ alignSelf: 'flex-start' }}>
              <Icon name="download" /> {leyendo ? 'Leyendo…' : 'Elegir archivo (.xlsx, .csv o .vcf)'}
              <input id="imp-archivo" type="file" accept=".xlsx,.csv,.vcf,text/csv,text/vcard" hidden onChange={(e) => void elegir(e.target.files?.[0])} />
            </label>
          </>
        ) : (
          <>
            <p style={{ margin: 0 }}>
              <b>{rev.archivo}</b>
            </p>
            <ul className="imp-resumen">
              <li>
                <b>{rev.nuevos.length}</b> contactos nuevos para traer
              </li>
              <li>
                <b>{rev.repetidos}</b> ya están en el CRM o repetidos en el archivo (se saltan)
              </li>
              <li>
                <b>{rev.incompletos}</b> sin correo ni teléfono (se saltan)
              </li>
            </ul>
            {rev.nuevos.length ? (
              <div className="imp-muestra">
                {rev.nuevos.slice(0, 5).map((c, i) => (
                  <span key={i}>
                    {`${c.nombre} ${c.apellido}`.trim()} · {c.email || c.telefono}
                    {c.empresa ? ` · ${c.empresa}` : ''}
                  </span>
                ))}
                {rev.nuevos.length > 5 ? <span className="note">y {rev.nuevos.length - 5} más…</span> : null}
              </div>
            ) : null}
            {avance ? (
              <div className="imp-avance">
                <progress max={avance.total} value={avance.hechos} />
                <span>
                  {avance.hechos} de {avance.total}
                  {avance.fallidos.length ? ` · ${avance.fallidos.length} no se pudieron crear` : ''}
                </span>
              </div>
            ) : null}
            {terminado && avance.fallidos.length ? <p className="note">No se crearon: {avance.fallidos.slice(0, 10).join(', ')}</p> : null}
          </>
        )}
        {error ? <p className="error">{error}</p> : null}
        <div className="actions">
          {rev && !avance ? (
            <button className="btn ghost" onClick={() => setRev(null)}>
              Elegir otro archivo
            </button>
          ) : null}
          <button className="btn ghost" onClick={onClose} disabled={!!avance && !terminado}>
            {terminado ? 'Listo' : 'Cerrar'}
          </button>
          {rev && !avance && rev.nuevos.length ? (
            <button className="btn primary" onClick={() => void importar()}>
              Importar {rev.nuevos.length} contactos
            </button>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}
